import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  ArrowRightIcon,
  ChartBarIcon,
  HeartIcon,
  HeartStraightIcon,
  LinkSimpleIcon,
  MagnifyingGlassIcon,
  RocketIcon,
  SparkleIcon,
  UsersIcon,
} from '@phosphor-icons/react'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_dashboard/upcoming-features')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="px-8 py-12">
        <div className="mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            {/* Animated Rocket Icon */}
            <div className="relative mb-8 flex justify-center mt-8">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full blur-3xl opacity-30 animate-pulse max-w-4xl mx-auto"></div>
              <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full shadow-2xl animate-bounce">
                <RocketIcon className="w-12 h-12 text-white animate-bounce" />
              </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
              <SparkleIcon className="w-4 h-4" />
              Coming Soon
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Exciting Features
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {' '}
                On The Horizon
              </span>
            </h1>

            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8 leading-relaxed">
              With your support and feedback, we're building powerful new
              features that will revolutionize your content marketing workflow.
              Here's what's coming next:
            </p>

            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-semibold text-gray-700 shadow-lg">
              <HeartIcon className="w-4 h-4 text-red-500" />
              Powered by community feedback
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {/* Enhanced Keyword Research */}
            <Card className="relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <MagnifyingGlassIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                      Premium
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Enhanced Keyword Research
                </CardTitle>
                <CardDescription className="text-gray-600 text-base">
                  Advanced keyword discovery and analysis powered by
                  industry-leading APIs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Ahrefs API integration for comprehensive keyword data
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Google Ads keyword planner integration
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Competitor keyword analysis and gap identification
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Search volume trends and seasonality insights
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                  <p className="text-sm text-blue-700 font-medium">
                    💡 Get keyword difficulty scores, CPC estimates, and search
                    intent classification
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Google Search Console Integration */}
            <Card className="relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <ChartBarIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      Analytics
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Google Search Console Integration
                </CardTitle>
                <CardDescription className="text-gray-600 text-base">
                  In-dashboard analytics and improved article research
                  capabilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 flex flex-col">
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Real-time search performance metrics
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Top performing pages and queries analysis
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Click-through rate optimization insights
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Content gap identification for new topics
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                  <p className="text-sm text-emerald-700 font-medium">
                    📊 Track your content's search performance and discover new
                    opportunities
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quality Backlink Exchange */}
            <Card className="relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-500"></div>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <LinkSimpleIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                      Network
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Quality Backlink Exchange
                </CardTitle>
                <CardDescription className="text-gray-600 text-base">
                  Leverage Rocket Rank's network of blogs for strategic link
                  building
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Access to curated network of high-authority blogs
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Automated backlink exchange matching
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Quality score assessment for potential partners
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Backlink tracking and performance analytics
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl">
                  <p className="text-sm text-orange-700 font-medium">
                    🔗 Build quality backlinks through our trusted network of
                    partners
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* User Management & Collaboration */}
            <Card className="relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-indigo-500"></div>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <UsersIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                      Team
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  User Management & Collaboration
                </CardTitle>
                <CardDescription className="text-gray-600 text-base">
                  Add team members, manage permissions, and collaborate on
                  content with approval workflows
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Invite team members and manage user roles
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Multi-level approval workflows for content
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Real-time collaborative editing capabilities
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Comment system and revision tracking
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">
                      Role-based permissions and access control
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl">
                  <p className="text-sm text-purple-700 font-medium">
                    👥 Scale your content team with powerful collaboration tools
                    and streamlined approval processes
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* More Coming Soon Section */}
          <div className="text-center mb-16">
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-12 shadow-2xl border border-white/20">
              <div className="flex items-center justify-center gap-3 mb-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  And Much More Coming Soon!
                </h2>
              </div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We're constantly working on new features based on your feedback.
                Stay tuned for more exciting updates that will enhance your
                content marketing workflow.
              </p>
            </div>
          </div>

          {/* Call to Action Section */}
          <div className="bg-gradient-to-tl from-purple-600 via-pink-600 to-indigo-800 rounded-3xl p-12 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-6">
                <h2 className="text-4xl font-bold">
                  Your Support Makes This Possible
                </h2>
              </div>

              <p className="text-xl mb-8 opacity-90 max-w-4xl mx-auto leading-relaxed">
                Every feature we build is inspired by your needs and feedback.
                Your continued support and engagement help us prioritize
                development and create tools that truly make a difference in
                your content marketing success.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  size="lg"
                  className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold shadow-lg"
                  asChild
                >
                  <a href="mailto:sam@userocketrank.com">
                    Let Us Know Your Ideas
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>

              <div className="mt-8 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium">
                With{' '}
                <span className="font-bold">
                  <HeartStraightIcon className="w-4 h-4" weight="fill" />
                </span>{' '}
                from Sam, the creator of Rocket Rank
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
